import {
  HEALTH_TIMINGS,
  createFiscalAgent,
  fiscalSupportOf,
  isLocalAgentError,
  newOperationId,
  sellGate,
  verdictOf,
  type DeviceSnapshot,
  type CardResolution,
  type ContinuePrintResult,
  type CorrectionRequest,
  type FiscalAgent,
  type FiscalError,
  type FiscalResult,
  type JournalEntry,
  type LocalAgentClient,
  type PrintResult,
  type ReceiptDraft,
  type ShiftTotals,
  type SaleGate,
  type SaleVerdict,
  type VerdictMeta,
  activeAgent,
  applyDiscovery,
  decodeAgentHelloV2,
  initialLink,
  linkPhase,
  type AgentRef,
  type LinkSnapshot,
} from '@krp/client-runtime'

/**
 * Сессия продажи через кассовый агент на этом компьютере: находит агента, держит токен рабочего места, следит за кассой
 * (порог «можно ли продавать») и выполняет чек, смену, наличные и восстановление. Контроллер не знает React и не знает, что
 * продаётся: ему приходит готовый чек, он возвращает итог (`SaleVerdict`). Состояние этого компьютера, а не данные сервера:
 * в кэш запросов и в хранилище не попадает.
 *
 * Правила безопасности денег:
 * - автоматически — только чтение (поиск агента, состояние кассы, журнал); команда кассе идёт только по действию человека;
 * - ключ операции создаётся один раз на попытку; повтор после обрыва связи идёт с ТЕМ ЖЕ ключом и только если чек тот же;
 * - обрыв связи при команде — «не знаем»: чек сам не повторяется, исход выясняется у агента (`operation`), а пока он неизвестен,
 *   продажа закрыта; 404 агента значит «до журнала не дошло» — чек не пробит;
 * - **долг продажу не закрывает** (решение владельца 18.10.2026): запись «деньги взяты, чека нет» (в том числе отложенная) кассу не
 *   блокирует; закрывает продажу только техническая занятость аппаратуры (`409 device-busy` с `retryAfter`) и короткое «выясняем исход
 *   команды после обрыва связи» (`pendingUnknown`), которое снимается ответом агента;
 * - токен хранится только в памяти этого контроллера; не принял даже обновлённый — продажа закрыта, команды не повторяются.
 */

/** Срок повтора после `409 device-busy`, если агент его не назвал, секунд. */
export const DEFAULT_BUSY_RETRY_SECONDS = 5
/** Токен обновляется заранее: за столько до конца срока, мс. */
export const TOKEN_RENEW_BEFORE_MS = 60_000
/** Сколько раз и через какие паузы выясняем исход чека после обрыва связи, мс. */
export const SETTLE_DELAYS_MS: readonly number[] = [1_000, 3_000, 8_000]

export type BusyKind = 'receipt' | 'shift' | 'cash' | 'recover' | 'report' | 'word'

export interface WorkplaceToken {
  token: string
  /** Когда токен перестанет приниматься агентом, мс (Unix). */
  expiresAt: number
}

/** Исход слова человека: итог операции или отказ агента его же словами (`code` — машинный код отказа, если был). */
export type WordOutcome =
  | { kind: 'result'; verdict: SaleVerdict }
  | { kind: 'refused'; code: string | null; text: string; action: string | null }

export interface SessionNotice {
  tone: 'info' | 'error'
  text: string
}

export interface Cashier {
  name: string
  inn?: string
  /** GUID узла `cashier` структуры ресурса Receipts (RP-OWN8); без него чек на сервере ждёт кассира (`pending-cashier`). */
  cashierId?: string
}

export interface AgentSessionModel {
  link: LinkSnapshot
  /** Агент, с которым работаем (выбранный или единственный); `null` — нет. */
  agent: AgentRef | null
  device: DeviceSnapshot | null
  deviceUnreachable: boolean
  tokenRefused: boolean
  /** Операции агента с неизвестным исходом (по его журналу), в том числе отложенные. Только для показа: продажу не закрывают. */
  inDoubt: readonly JournalEntry[]
  /**
   * Первая неразобранная и НЕ отложенная продажа из журнала (после перезагрузки страницы или чужого окна) целиком, со словами
   * агента; `null` — таких нет. Окно показывает по ней ход проведения с действиями.
   */
  unresolved: SaleVerdict | null
  /** Чек, исход которого неизвестен из-за обрыва связи: его ключ. */
  pendingUnknown: string | null
  busy: BusyKind | null
  gate: SaleGate
  /** Итог последней операции с деньгами. */
  last: SaleVerdict | null
  notice: SessionNotice | null
  /** Первый просмотр портов закончен. */
  observed: boolean
}

export interface AgentSessionDeps {
  client: LocalAgentClient
  /** Выдаёт платформа: `POST /api/platform/v1/me/agent-token` для запущенного агента (`instanceId`). */
  fetchToken: (agentInstanceId: string) => Promise<WorkplaceToken>
  siteOrigin: string
  minApiLevel: number
  onChange: (model: AgentSessionModel) => void
  clock?: () => number
}

export type ReceiptRequestDraft = Omit<ReceiptDraft, 'operationId'>

export interface AgentSessionActions {
  /** Перечитать всё сейчас: полный просмотр портов, кассу и журнал. */
  recheck: () => Promise<void>
  pickAgent: (port: number) => void
  /** Чек или возврат. `null` — продажа закрыта, ничего не отправлено. */
  sell: (draft: ReceiptRequestDraft) => Promise<SaleVerdict | null>
  openShift: (cashier: Cashier) => Promise<SaleVerdict | null>
  closeShift: (cashier: Cashier) => Promise<SaleVerdict | null>
  cash: (type: 'in' | 'out', amount: number, cashier: Cashier) => Promise<SaleVerdict | null>
  /** Выяснить исход операции с неизвестным результатом по накопителю кассы. */
  recover: (operationId: string) => Promise<SaleVerdict | null>
  /** «Отложить продажу». `null` — агент недоступен или занят другой командой, ничего не отправлено. */
  park: (operationId: string) => Promise<WordOutcome | null>
  /** «Повторить проведение» одним нажатием; с номером последнего документа — запасной путь. */
  repunch: (operationId: string, lastFiscalDocument?: number) => Promise<WordOutcome | null>
  correction: (operationId: string, request: CorrectionRequest) => Promise<WordOutcome | null>
  resolveCard: (operationId: string, resolution: CardResolution) => Promise<WordOutcome | null>
  /** Человек отменил платёж на терминале сам: закрыть операцию. */
  confirmVoid: (operationId: string) => Promise<WordOutcome | null>
  /** «Допечатать»: чек заново не пробивается. */
  continuePrint: () => Promise<{ kind: 'result'; result: ContinuePrintResult } | { kind: 'refused'; text: string; action: string | null } | null>
  /** Итоги смены для экрана «Закрыть смену»: чтение, ничего не меняет. `null` — прочитать не удалось (причина в `notice`). */
  shiftTotals: () => Promise<ShiftTotals | null>
  printReportX: (cashier?: Cashier) => Promise<PrintResult | null>
  printLastReceiptCopy: () => Promise<PrintResult | null>
  dismissNotice: () => void
}

export interface AgentSessionController {
  getModel: () => AgentSessionModel
  start: () => void
  stop: () => void
  actions: AgentSessionActions
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

const failure = (code: string, text: string, action: string | null = null): FiscalError => ({ code, text, action })

export function createAgentSessionController(deps: AgentSessionDeps): AgentSessionController {
  const clock = deps.clock ?? (() => Date.now())
  let link = initialLink(deps.siteOrigin, deps.minApiLevel)
  let device: DeviceSnapshot | null = null
  let deviceUnreachable = false
  let deviceNote: string | null = null
  let tokenRefused = false
  let inDoubt: readonly JournalEntry[] = []
  let unresolved: SaleVerdict | null = null
  let pendingUnknown: string | null = null
  /** Агент ответил `409 device-busy`: до какого момента (часы контроллера) не пытаться и что он сказал. */
  let agentBusy: { until: number; device: string | null; text: string } | null = null
  let busy: BusyKind | null = null
  let last: SaleVerdict | null = null
  let notice: SessionNotice | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  let running = false
  let generation = 0
  let lastFullDiscovery = 0
  let lastJournalAt = 0
  let lastDeviceAt = 0
  /**
   * Попытка команды по виду (чек, смена, наличные): ключ и отпечаток запроса. Живёт, пока агент не дал определённого ответа:
   * повтор ТОГО ЖЕ запроса идёт с тем же ключом (второго документа не будет), другой запрос — с новым.
   */
  type KeyedKind = 'receipt' | 'shift' | 'cash'
  const attempts = new Map<KeyedKind, { operationId: string; fingerprint: string }>()
  let token: { instanceId: string; value: WorkplaceToken } | null = null
  let knownInstance: string | null = null
  /** Компьютер, на котором работает известный агент: перезапуск агента на нём не отменяет ключ ещё не выясненной операции. */
  let knownMachine: string | null = null

  const agentNow = (): AgentRef | null => activeAgent(link)

  const buildModel = (): AgentSessionModel => {
    const agent = agentNow()
    const phase = linkPhase(link, clock())
    const busyLeft = agentBusy === null ? 0 : (agentBusy.until - clock()) / 1000
    const busyView = agentBusy !== null && busyLeft > 0 ? { retryAfterSeconds: busyLeft, device: agentBusy.device, text: agentBusy.text } : null
    return {
      link,
      agent,
      device,
      deviceUnreachable,
      tokenRefused,
      inDoubt,
      unresolved,
      pendingUnknown,
      busy,
      gate: sellGate({ link: phase, fiscalSupport: agent === null ? undefined : fiscalSupportOf({ api: agent.hello.api, workplaceMode: agent.hello.workplaceMode }), tokenRefused, device, deviceUnreachable, deviceNote, agentBusy: busyView, settling: pendingUnknown !== null, busy: busy !== null }),
      last,
      notice,
      observed: link.probed,
    }
  }

  const emit = (): void => deps.onChange(buildModel())

  // ---- токен ----

  const getToken = async (renew: boolean): Promise<string> => {
    const agent = agentNow()
    if (agent === null) throw new Error('Кассовый агент не найден.')
    const instance = agent.hello.instanceId
    if (!renew && token !== null && token.instanceId === instance && token.value.expiresAt - TOKEN_RENEW_BEFORE_MS > clock()) {
      return token.value.token
    }
    try {
      const value = await deps.fetchToken(instance)
      token = { instanceId: instance, value }
      tokenRefused = false
      return value.token
    } catch (reason) {
      token = null
      tokenRefused = true
      notice = { tone: 'error', text: reason instanceof Error ? reason.message : 'Не удалось получить токен рабочего места.' }
      throw reason
    }
  }

  const fiscal: FiscalAgent = createFiscalAgent({
    client: deps.client,
    port: () => agentNow()?.port,
    token: getToken,
  })

  /** Ошибка запроса к агенту → что запомнить о токене. */
  const noteFailure = (reason: unknown): void => {
    if (isLocalAgentError(reason) && reason.kind === 'unauthorized') {
      tokenRefused = true
      token = null
    }
  }

  // ---- поиск агента и чтение состояния ----

  const resetForNewAgent = (keepOperation = false): void => {
    token = null
    tokenRefused = false
    device = null
    deviceUnreachable = false
    deviceNote = null
    inDoubt = []
    unresolved = null
    lastJournalAt = 0
    lastDeviceAt = 0
    // Тот же агент на том же компьютере после перезапуска: его журнал на диске пережил перезапуск, поэтому ключи попыток и операция
    // с неизвестным исходом остаются — исход выяснится по ключу у нового запуска. Другой агент ключа не знает.
    if (keepOperation) return
    attempts.clear()
    pendingUnknown = null
  }

  const discover = async (force: boolean, mine: number): Promise<void> => {
    const now = clock()
    const selected = link.selected
    let found: AgentRef[]
    if (!force && selected !== null && link.agents.length > 0 && now - lastFullDiscovery < 30_000) {
      const one = await deps.client.probe(selected.port, decodeAgentHelloV2)
      found = one === null ? [] : link.agents.map((agent) => (agent.port === one.port ? { port: one.port, hello: one.hello } : agent))
    } else {
      const list = await deps.client.discoverAll(decodeAgentHelloV2)
      found = list.map((item) => ({ port: item.port, hello: item.hello }))
      lastFullDiscovery = now
    }
    if (mine !== generation) return
    link = applyDiscovery(link, found, clock())
    const instance = agentNow()?.hello.instanceId ?? null
    const machine = agentNow()?.hello.host?.machine ?? null
    if (instance !== knownInstance) {
      // Другой агент или тот же, но перезапущенный: токен привязан к запуску, прежние данные не его.
      if (knownInstance !== null && instance !== null) resetForNewAgent(machine !== null && machine === knownMachine)
      knownInstance = instance
    }
    if (instance !== null) knownMachine = machine
  }

  const readDevice = async (): Promise<void> => {
    try {
      device = await fiscal.device()
      deviceUnreachable = false
      deviceNote = null
    } catch (reason) {
      noteFailure(reason)
      deviceUnreachable = true
      deviceNote = isLocalAgentError(reason) && reason.kind === 'refused' ? reason.message : null
    }
    lastDeviceAt = clock()
  }

  const readJournal = async (): Promise<void> => {
    try {
      inDoubt = await fiscal.journal({ status: 'in-doubt' })
      unresolved = await readUnresolved()
    } catch (reason) {
      // Журнал не прочитан — прежний список остаётся: «неразобранных нет» без ответа агента не объявляем.
      noteFailure(reason)
    }
    lastJournalAt = clock()
  }

  /** Первая неразобранная не отложенная ПРОДАЖА целиком: журнал даёт строку без реквизитов карты, полный итог — по ключу. */
  const readUnresolved = async (): Promise<SaleVerdict | null> => {
    const first = inDoubt.find((entry) => entry.kind === 'receipt' && !entry.parked)
    if (first === undefined) return null
    try {
      const result = await fiscal.operation(first.operationId)
      return result === null || result.status !== 'in-doubt' ? null : verdictOf(result)
    } catch (reason) {
      noteFailure(reason)
      // Подробности не прочитались: по строке журнала собираем честное «неизвестно» без реквизитов, не пряча запись.
      return {
        kind: 'unknown',
        error: first.error,
        card: null,
        meta: { operationId: first.operationId, notice: null, possibleDoubleReceipt: false, parking: null, canRepunch: null, attempt: null, printed: null },
      }
    }
  }

  const refreshAll = async (): Promise<void> => {
    await readDevice()
    await readJournal()
  }

  const connected = (): boolean => linkPhase(link, clock()) === 'connected' && agentNow() !== null

  const tick = async (force = false): Promise<void> => {
    const mine = generation
    try {
      await discover(force, mine)
      if (mine !== generation) return
      if (connected() && busy === null && !tokenRefused) {
        if (pendingUnknown !== null) {
          const id = pendingUnknown
          const settled = await settle(id, mine)
          if (settled !== null) {
            last = settled.verdict
            if (!settled.notDelivered) {
              for (const [kind, slot] of attempts) if (slot.operationId === id) attempts.delete(kind)
            }
          }
        }
        const now = clock()
        if (force || now - lastDeviceAt >= HEALTH_TIMINGS.agentDeviceEveryMs) await readDevice()
        if (force || now - lastJournalAt >= HEALTH_TIMINGS.agentJournalEveryMs || lastJournalAt === 0) await readJournal()
      }
    } finally {
      if (mine === generation) emit()
    }
  }

  const schedule = (mine: number): void => {
    if (!running || mine !== generation) return
    const delay = connected() ? 1_000 : HEALTH_TIMINGS.agentSearchEveryMs
    timer = setTimeout(() => {
      void tick().finally(() => schedule(mine))
    }, delay)
  }

  // ---- операции с деньгами ----

  /**
   * Выяснить исход операции, чей ответ потерян: спросить агента по ключу. `null` — выяснить пока нельзя.
   * `notDelivered` — агент о ключе не слышал: в журнал команда не попала. Это НЕ «наверняка не пробито»: запрос мог остаться в очереди
   * агента (он берёт кассу по одному) и выполниться позже. Поэтому повтор допустим только с ТЕМ ЖЕ ключом — тогда второго чека не будет.
   */
  const operationMeta = (operationId: string): VerdictMeta => ({
    operationId, notice: null, possibleDoubleReceipt: false, parking: null, canRepunch: null,
    attempt: null, printed: null, stage: null, oneCStage: null,
  })

  const settle = async (operationId: string, mine = generation): Promise<{ verdict: SaleVerdict; notDelivered: boolean } | null> => {
    try {
      const result = await fiscal.operation(operationId)
      if (mine !== generation) return null
      pendingUnknown = null
      if (result === null) {
        return {
          verdict: { kind: 'not-sold', error: failure('not-delivered', 'Команда не дошла до кассы, чек не пробит. Можно повторить тот же чек.'), card: null, meta: operationMeta(operationId) },
          notDelivered: true,
        }
      }
      return { verdict: verdictOf(result), notDelivered: false }
    } catch (reason) {
      noteFailure(reason)
      return null
    }
  }

  const unknownAfterBreak = (operationId: string): SaleVerdict => ({
    kind: 'unknown',
    error: failure('agent-unreachable', 'Связь с кассовым агентом оборвалась во время операции. Не повторяйте чек: проверьте кассу и восстановите исход.'),
    card: null,
    meta: operationMeta(operationId),
  })

  /**
   * Одна команда с деньгами с общими правилами обрыва. `call` получает ключ операции. `keepKey` — ключ надо сохранить для повтора
   * ТОГО ЖЕ чека: агент не дал определённого ответа (обрыв, «не дошло» без гарантии), и новый ключ мог бы дать второй чек.
   */
  const runFiscal = async (
    kind: BusyKind,
    operationId: string,
    call: (operationId: string) => Promise<FiscalResult>,
  ): Promise<{ verdict: SaleVerdict; keepKey: boolean }> => {
    const mine = generation
    busy = kind
    notice = null
    emit()
    let verdict: SaleVerdict
    let keepKey = false
    try {
      verdict = verdictOf(await call(operationId))
      pendingUnknown = null
    } catch (reason) {
      noteFailure(reason)
      if (isLocalAgentError(reason) && reason.kind === 'refused' && reason.status === 409 && reason.code === 'device-busy') {
        // Техническая занятость: агент ничего не начинал. Запоминаем срок, чтобы продажа не стучалась раньше времени.
        agentBusy = { until: clock() + (reason.retryAfterSeconds ?? DEFAULT_BUSY_RETRY_SECONDS) * 1000, device: reason.busyDevice ?? null, text: reason.message }
        verdict = { kind: 'not-sold', error: failure('device-busy', reason.message), card: null }
      } else if (isLocalAgentError(reason) && reason.kind === 'refused' && reason.status !== 404 && reason.status !== 503) {
        // Агент понял запрос и отказал до кассы (проверка чека, конфликт ключа): чек не пробит.
        verdict = { kind: 'not-sold', error: failure(reason.code ?? 'refused', reason.message), card: null }
      } else if (isLocalAgentError(reason) && reason.kind === 'refused' && reason.status === 503) {
        verdict = { kind: 'not-sold', error: failure(reason.code ?? 'no-device', reason.message), card: null }
      } else if (isLocalAgentError(reason) && reason.kind === 'unauthorized') {
        // 401 = команда не выполнялась (токен не принят и после обновления).
        verdict = { kind: 'not-sold', error: failure('unauthorized', 'Агент не принял токен рабочего места. Чек не пробит.'), card: null }
      } else if (!isLocalAgentError(reason)) {
        // Токен не получен: до агента команда не дошла.
        verdict = { kind: 'not-sold', error: failure('token-unavailable', reason instanceof Error ? reason.message : 'Не получен токен рабочего места. Чек не пробит.'), card: null }
      } else {
        // Обрыв связи или невнятный ответ: чек мог быть пробит. Сами не повторяем — спрашиваем агента по ключу.
        pendingUnknown = operationId
        verdict = unknownAfterBreak(operationId)
        keepKey = true
        for (const delay of SETTLE_DELAYS_MS) {
          await sleep(delay)
          if (mine !== generation) break
          const settled = await settle(operationId, mine)
          if (settled !== null) {
            verdict = settled.verdict
            keepKey = settled.notDelivered
            break
          }
        }
      }
    }
    busy = null
    // `last` — исход ЧЕКА продажи: по нему окно снимает неизвестный исход. Внесение, выемка и смена его не трогают (свой итог они возвращают),
    // иначе «внесение выполнено» при неизвестном чеке показало бы продажу пробитой с номером документа внесения.
    if (kind === 'receipt') last = verdict
    if (mine === generation) {
      await refreshAll()
      emit()
    }
    return { verdict, keepKey }
  }

  const mayRunMoneyCommand = (allowClosedShift: boolean): boolean => {
    const gate = buildModel().gate
    if (gate.open) return true
    if (allowClosedShift && (gate.reason === 'shift-closed' || gate.reason === 'shift-expired')) return true
    notice = { tone: 'error', text: gate.text }
    emit()
    return false
  }

  /** Команда с ключом попытки: тот же запрос — тот же ключ, пока агент не ответил определённо. */
  const keyedRun = async (kind: KeyedKind, request: unknown, call: (operationId: string) => Promise<FiscalResult>): Promise<SaleVerdict> => {
    const fingerprint = JSON.stringify(request)
    let slot = attempts.get(kind)
    if (slot === undefined || slot.fingerprint !== fingerprint) {
      slot = { operationId: newOperationId(), fingerprint }
      attempts.set(kind, slot)
    }
    const used = slot
    const { verdict, keepKey } = await runFiscal(kind, used.operationId, call)
    if (!keepKey && attempts.get(kind) === used) attempts.delete(kind)
    return verdict
  }

  const actions: AgentSessionActions = {
    recheck: () => tick(true),
    pickAgent: (port) => {
      const found = link.agents.find((agent) => agent.port === port)
      if (found === undefined) return
      link = { ...link, selected: found }
      knownInstance = found.hello.instanceId
      knownMachine = found.hello.host?.machine ?? null
      resetForNewAgent()
      emit()
      void tick(true)
    },
    async sell(draft) {
      if (!mayRunMoneyCommand(false)) return null
      return keyedRun('receipt', draft, (operationId) => fiscal.receipt({ ...draft, operationId }))
    },
    async openShift(cashier) {
      if (!mayRunMoneyCommand(true)) return null
      return keyedRun('shift', { type: 'open', cashier }, (operationId) => fiscal.shift({ operationId, type: 'open', cashier }))
    },
    async closeShift(cashier) {
      if (!mayRunMoneyCommand(true)) return null
      return keyedRun('shift', { type: 'close', cashier }, (operationId) => fiscal.shift({ operationId, type: 'close', cashier }))
    },
    async cash(type, amount, cashier) {
      if (!mayRunMoneyCommand(false)) return null
      return keyedRun('cash', { type, amount, cashier }, (operationId) => fiscal.cash({ operationId, type, amount, cashier }))
    },
    async recover(operationId) {
      if (!connected() || busy !== null) return null
      busy = 'recover'
      notice = null
      emit()
      let verdict: SaleVerdict | null = null
      try {
        verdict = verdictOf(await fiscal.recover(operationId))
        if (pendingUnknown === operationId) pendingUnknown = null
        // Исход определён — ключ попытки этой операции больше не нужен (как при автоматическом снятии): иначе следующая продажа с такой же
        // корзиной пошла бы с прежним ключом, агент вернул бы старый итог, и окно показало бы «продано» без нового чека и без оплаты.
        if (verdict.kind !== 'unknown') for (const [kind, slot] of attempts) if (slot.operationId === operationId) attempts.delete(kind)
        last = verdict
      } catch (reason) {
        noteFailure(reason)
        notice = { tone: 'error', text: isLocalAgentError(reason) ? reason.message : 'Не удалось выяснить исход операции.' }
      }
      busy = null
      await refreshAll()
      emit()
      return verdict
    },
    park: (operationId) => humanWord((api) => api.park(operationId)),
    repunch: (operationId, lastFiscalDocument) => humanWord((api) => api.repunch(operationId, lastFiscalDocument)),
    correction: (operationId, request) => humanWord((api) => api.correction(operationId, request)),
    resolveCard: (operationId, resolution) => humanWord((api) => api.resolveCard(operationId, resolution)),
    confirmVoid: (operationId) => humanWord((api) => api.confirmVoid(operationId)),
    async continuePrint() {
      if (!connected() || busy !== null) return null
      busy = 'word'
      notice = null
      emit()
      try {
        return { kind: 'result' as const, result: await fiscal.continuePrint() }
      } catch (reason) {
        noteFailure(reason)
        const shown = refusalOf(reason)
        return { kind: 'refused' as const, text: shown.text, action: null }
      } finally {
        busy = null
        await refreshAll()
        emit()
      }
    },
    async shiftTotals() {
      if (!connected()) return null
      try {
        return await fiscal.shiftTotals()
      } catch (reason) {
        noteFailure(reason)
        notice = { tone: 'error', text: isLocalAgentError(reason) ? reason.message : 'Не удалось прочитать итоги смены.' }
        emit()
        return null
      }
    },
    async printReportX(cashier) {
      return printing(() => fiscal.printReportX(cashier))
    },
    async printLastReceiptCopy() {
      return printing(() => fiscal.printLastReceiptCopy())
    },
    dismissNotice: () => {
      notice = null
      emit()
    },
  }

  /** Отказ или обрыв при слове человека → слова для кассира. Обрыв — «не знаем», не «не вышло»: повтор вслепую не предлагается. */
  function refusalOf(reason: unknown): { code: string | null; text: string } {
    if (isLocalAgentError(reason)) {
      if (reason.kind === 'unreachable') return { code: 'agent-unreachable', text: 'Связь с кассовым агентом оборвалась во время команды. Результат неизвестен: проверьте кассу и журнал, не повторяйте вслепую.' }
      if (reason.kind === 'unauthorized') return { code: 'unauthorized', text: 'Агент не принял токен рабочего места. Команда не выполнена.' }
      return { code: reason.code ?? null, text: reason.message }
    }
    return { code: null, text: reason instanceof Error ? reason.message : 'Команда не выполнена.' }
  }

  /** Слово человека агенту (отложить, повторить, коррекция…): одна команда, без повторов; итог и журнал перечитываются. */
  async function humanWord(call: (api: FiscalAgent) => Promise<FiscalResult>): Promise<WordOutcome | null> {
    if (!connected() || busy !== null) return null
    busy = 'word'
    notice = null
    emit()
    let outcome: WordOutcome
    try {
      const verdict = verdictOf(await call(fiscal))
      last = verdict
      outcome = { kind: 'result', verdict }
    } catch (reason) {
      noteFailure(reason)
      const shown = refusalOf(reason)
      outcome = { kind: 'refused', code: shown.code, text: shown.text, action: null }
    }
    busy = null
    await refreshAll()
    emit()
    return outcome
  }

  async function printing(run: () => Promise<PrintResult>): Promise<PrintResult | null> {
    if (!connected() || busy !== null) return null
    busy = 'report'
    notice = null
    emit()
    let result: PrintResult | null = null
    try {
      result = await run()
      if (result.status === 'failed') notice = { tone: 'error', text: result.error?.text ?? 'Печать не удалась.' }
    } catch (reason) {
      noteFailure(reason)
      notice = { tone: 'error', text: isLocalAgentError(reason) ? reason.message : 'Печать не удалась.' }
    }
    busy = null
    emit()
    return result
  }

  return {
    getModel: buildModel,
    start() {
      if (running) return
      running = true
      generation += 1
      const mine = generation
      void tick(true).finally(() => schedule(mine))
    },
    stop() {
      running = false
      generation += 1
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    },
    actions,
  }
}
